import { Fragment, useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding, useContentToken, useProperty } from '@vibesites/runtime/react';
import { getEligibleNavPages } from './_navShared';

interface VirtualTour {
  virtual_tour_url?: string | null;
}

export function SitemapContent() {
  // Mirror the header's "pages in navigation" set exactly (see ./_navShared),
  // so the sitemap lists the same pages the visitor can reach from the nav.
  const virtualTours = useCollectionBinding<VirtualTour>('sitemapcontent.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map appears in the nav only when the property has the
  // ApartmentSync (Repli360 / RRAC) IDs to drive its widget, or an explicit
  // editor override on the header. Replicate that check so the sitemap doesn't
  // surface a page the nav hides. (Custom fields are read via useProperty; the
  // override token is header-scoped and read-only here.)
  const property = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = property?.custom_apartmentsync_javascript_id as string | null | undefined;
  const apartmentSyncConfigId = property?.custom_apartmentsync_config_id as string | null | undefined;
  const interactiveMapShowIfExists = useContentToken('header.interactive_map_show_if_exists');
  const hasInteractiveLeasing =
    (!!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
      !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim())) ||
    !!(interactiveMapShowIfExists && String(interactiveMapShowIfExists).trim());

  const pages = useMemo(
    () => getEligibleNavPages(hasVirtualTours, hasInteractiveLeasing).map(([, page]) => page),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  return (
    <section className="py-12 md:py-16 bg-[var(--vibe-color-background)]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {pages.map((page, index) => {
          // Trim the space above the "Floor Plans" entry by 30px.
          const isFloorPlans = page.name.trim().toLowerCase() === 'floor plans';
          return (
            <Fragment key={page.path}>
              {index > 0 && (
                <hr className="mx-auto w-12 border-t border-[var(--vibe-color-border)]" />
              )}
              <a
                href={page.path}
                className={`block text-center font-heading text-lg md:text-2xl font-normal text-[var(--vibe-color-primary)] py-3 hover:opacity-80 transition-opacity${
                  isFloorPlans ? ' -mt-[30px]' : ''
                }`}
              >
                {page.name}
              </a>
            </Fragment>
          );
        })}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {},
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(SitemapContent as any).contentSchema = contentSchema;

export default SitemapContent;
